import { useEffect, useState } from 'react';
import { ButtonContainer } from '@/components/global';
import { useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { cn } from '@/utils/cn';
import { loadFonts } from '@/utils/loadFonts';
import { Button, FlexContainer } from '@cof/omni-react';
import styled from '@emotion/styled';

const FONT_BASE_PATH = '/client-config/fonts/';
const AUTO_REDIRECT_SECONDS = 3;

/**
 * ease-av theme colors (src/providers/ConfigProvider/mockAVConfigV1.ts,
 * theme.background/theme.text) scoped locally via CSS variable overrides.
 * OneTap has no session, so it never goes through ConfigProvider/
 * ThemeWrapper — without this, the button falls back to theme.css's
 * global defaults, where the button background and its own text are both
 * near-identical blues (#0070e3 bg vs #0072c4 text) and the label
 * disappears. These values restore ease-av's actual contrast (dark navy
 * button, white text).
 */
const OneTapTheme = styled('div')`
  --bg-interactive-emphasis-high: #013d5b;
  --bg-interactive-emphasis-high-hover: #032740;
  --bg-interactive-emphasis-high-press: #031e36;
  --text-neutral-primary: #14110d;
  --text-neutral-inverse: #ffffff;
  font-family: 'Optimist', sans-serif;

  @media (prefers-color-scheme: dark) {
    --bg-interactive-emphasis-high: #77b2c7;
    --bg-interactive-emphasis-high-hover: #8ec0d1;
    --bg-interactive-emphasis-high-press: #a6cbd6;
    --text-neutral-primary: #ffffff;
    --text-neutral-inverse: #0e1012;
  }
`;

const OneTapResultTitle = styled('h3')`
  font-size: 25px;
  font-weight: 600;
  padding: 0 32px;
  align-self: flex-start;
`;

const OneTapResultHeader = styled('header')`
  width: 100vw;
  padding: 32px 32px 32px;
  display: flex;
  justify-content: center;
`;

const OneTapResultBody = styled('div')`
  white-space: pre-line;
  padding: 0px 32px 32px;
  align-self: flex-start;
`;

const OneTapRedirectHint = styled('p')`
  font-size: 14px;
  padding: 0 32px 16px;
  align-self: flex-start;
  opacity: 0.8;
`;

const COPY = {
  success: {
    icon: '/assets/success.svg',
    title: 'Your card is verified',
    body: 'OneTap confirmed this card belongs to you. This helps us protect your account.',
  },
  fail: {
    icon: '/assets/failure.svg',
    title: "We couldn't verify your card",
    body: 'OneTap was unable to confirm this card tap. Please try tapping your card again.',
  },
};

/**
 * Config-free OneTap result screen. Deliberately does NOT reuse
 * MessageScreen — that component depends on useConfig()/useDevice(),
 * neither of which exist on the sessionless /tap/:cardType route.
 */
export const OneTapResult = ({
  variant,
  redirectUrl,
}: {
  variant: 'success' | 'fail';
  redirectUrl?: string;
}) => {
  useAirKeyTracePageView(
    variant === 'success' ? 'onetap_success' : 'onetap_fail',
  );

  loadFonts('Optimist, sans-serif', FONT_BASE_PATH);

  const { icon, title, body } = COPY[variant];

  const [secondsLeft, setSecondsLeft] = useState(AUTO_REDIRECT_SECONDS);

  const handleRedirect = () => {
    if (redirectUrl) {
      window.location.assign(redirectUrl);
    }
  };

  // Auto-redirect after a short countdown. Universal Links generally need a
  // real user gesture to switch apps on iOS — a JS-triggered navigation with
  // no click often just opens the web fallback instead of the native app.
  // The visible "Continue to app" button stays up the whole time as a
  // manual fallback for exactly that case.
  useEffect(() => {
    if (!redirectUrl) return;

    if (secondsLeft <= 0) {
      handleRedirect();
      return;
    }

    const timeout = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectUrl, secondsLeft]);

  return (
    <OneTapTheme>
      <FlexContainer
        data-testid="OneTapResult"
        className={cn('bg-page')}
        direction="column"
        alignItems="center"
        padding="large"
      >
        <FlexContainer direction="column" alignItems="center">
          <OneTapResultHeader data-testid="OneTapResult--Header">
            <img
              src={'/assets/C1.svg'}
              width={96}
              alt="logo"
              role="presentation"
            />
          </OneTapResultHeader>

          <img
            src={icon}
            width={96}
            alt="icon"
            role="presentation"
            data-testid={`onetap-${variant}-icon`}
          />

          <OneTapResultTitle className={cn('text-neutral-primary')}>
            {title}
          </OneTapResultTitle>

          <OneTapResultBody className={cn('text-neutral-primary')}>
            {body}
          </OneTapResultBody>

          {redirectUrl && (
            <>
              <OneTapRedirectHint
                className={cn('text-neutral-primary')}
                data-testid="OneTapResult--RedirectHint"
              >
                {secondsLeft > 0
                  ? `Redirecting in ${secondsLeft}...`
                  : 'Redirecting...'}
              </OneTapRedirectHint>
              <ButtonContainer style={{ width: '100%', maxWidth: '400px' }}>
                <Button
                  size="medium"
                  width="100%"
                  rank="primary"
                  className={cn(
                    'text-neutral-inverse',
                    'bg-interactive-emphasis-high',
                  )}
                  onClick={handleRedirect}
                >
                  Continue to app
                </Button>
              </ButtonContainer>
            </>
          )}
        </FlexContainer>
      </FlexContainer>
    </OneTapTheme>
  );
};
